import { getRole, Roles } from 'api/tokenStorage';
import { PageHead } from 'ui/kit/PageHead';
import { FormContainer } from 'components/formsV2/formContainer/index';
import { useRemoveUserRole } from 'hooks/useRemoveUserRole';
import { useState } from 'react';
import { ConfirmModal } from 'ui';
import { DesktopNotifySettings } from 'components/DesktopNotifyPrompt/DesktopNotifySettings';
import { formatEmailDisplay, formatPhoneDisplay } from 'modules/Profile/components/contactUtils';
import { Link } from 'react-router';
import classes from './t3Screen.module.scss';
import { TutorVkSettings } from './TutorVkSettings';
import { PersonalDataChangeRequestSection } from 'modules/Profile/components/PersonalDataChangeRequestSection';
import { PasswordRulesHint } from 'components/PasswordRulesHint';
import {
  passwordApiFieldError,
  useChangePassword,
  useSetPassword,
} from 'hooks/usePasswordChange';
import type { FormikHelpers } from 'formik';
import { AUTH_SESSION_QUERY_KEY, useAuthSession } from 'hooks/useAuthSession';
import { useQueryClient } from '@tanstack/react-query';
import {
  PASSWORD_MAX_LENGTH,
  confirmPasswordFieldSchema,
  newPasswordFieldSchema,
} from 'schemas/passwordRules';
import * as yup from 'yup';

type PasswordFormValues = {
  old_password: string;
  new_password: string;
  repeat_new_password: string;
};

const changePasswordSchema = yup.object().shape({
  old_password: yup
    .string()
    .required('Введите текущий пароль')
    .max(
      PASSWORD_MAX_LENGTH,
      `Пароль не должен превышать ${PASSWORD_MAX_LENGTH} символов`,
    ),

  new_password: newPasswordFieldSchema({ requiredMessage: 'Введите новый пароль' }).notOneOf(
    [yup.ref('old_password')],
    'Новый пароль должен отличаться от старого',
  ),

  repeat_new_password: confirmPasswordFieldSchema('new_password'),
});

const setPasswordSchema = yup.object().shape({
  new_password: newPasswordFieldSchema({ requiredMessage: 'Введите пароль' }),
  repeat_new_password: confirmPasswordFieldSchema('new_password'),
});

export const TeacherSettings = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const removeRole = useRemoveUserRole();
  const roleDict = getRole();
  const roleId = roleDict ? Roles[roleDict as keyof typeof Roles] : undefined;
  const changePasswordRequest = useChangePassword();
  const setPasswordRequest = useSetPassword();
  const { data: session } = useAuthSession();
  const queryClient = useQueryClient();
  const needsSetPassword = session?.has_password === false;

  const CloseModal = () => {
    setModalOpen(false);
  };

  const initialValues = {
    old_password: '',
    repeat_new_password: '',
    new_password: '',
  };

  const handleSubmit = (values: PasswordFormValues, helpers: FormikHelpers<PasswordFormValues>) => {
    // Ошибки API, относящиеся к полю (неверный текущий пароль, «должен отличаться»),
    // показываем под этим полем, а не всплывашкой.
    const showFieldError = (error: unknown) => {
      const fieldError = passwordApiFieldError(error);
      if (!fieldError) return;
      helpers.setFieldTouched(fieldError.field, true, false);
      helpers.setFieldError(fieldError.field, fieldError.message);
    };
    if (needsSetPassword) {
      setPasswordRequest.mutate(
        {
          new_password: values.new_password,
          repeat_new_password: values.repeat_new_password,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
          },
          onError: showFieldError,
        },
      );
      return;
    }
    changePasswordRequest.mutate(values, { onError: showFieldError });
  };

  return (
    <main className={classes.page}>
      <PageHead crumb={<>Личный кабинет / Настройки</>} title={<>Контакты и доступ</>} />

      <section className={classes.card}>
        <h2 className={classes.cardTitle} data-icon="contacts">Контакты</h2>
        <div className={classes.row}>
          <div className={classes.cell}>
            <p className={classes.label}>Почта</p>
            <p className={classes.value}>
              <span className={classes.valueEllipsis} title={session?.email || undefined}>
                {formatEmailDisplay(session?.email)}
              </span>
            </p>
          </div>
          <div className={classes.cell}>
            <p className={classes.label}>Телефон</p>
            <p className={classes.value}>{formatPhoneDisplay(session?.phone)}</p>
          </div>
        </div>
        <p className={classes.muted}>
          Почту и телефон нельзя поменять самостоятельно. Если данные устарели, отправьте заявку
          ниже.
        </p>
      </section>

      <FormContainer<PasswordFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={needsSetPassword ? setPasswordSchema : changePasswordSchema}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize
      >
        {({ values, errors, touched, handleBlur, setFieldValue, setFieldTouched }) => (
          <section className={classes.card}>
            <h2 className={classes.cardTitle} data-icon="lock">Пароль</h2>
            <p className={classes.muted}>
              {needsSetPassword
                ? 'Задайте пароль, если хотите входить не только по коду.'
                : 'Пароль скрыт. Заполните поля, если хотите его сменить.'}
            </p>
            {needsSetPassword ? null : (
              <label className={classes.field}>
                <span className={classes.label}>Текущий пароль</span>
                <input
                  className={classes.control}
                  type='password'
                  name='old_password'
                  placeholder='Текущий пароль'
                  value={values.old_password}
                  onChange={(event) => {
                    setFieldValue('old_password', event.target.value);
                    if (errors.old_password) setFieldTouched('old_password', false);
                  }}
                  onBlur={handleBlur}
                />
                {touched.old_password && errors.old_password ? (
                  <p className={`fig-field-error ${classes.error}`} role='alert'>
                    {errors.old_password}
                  </p>
                ) : null}
              </label>
            )}
            <div className={classes.row}>
              <label className={classes.cell}>
                <span className={classes.label}>Новый пароль</span>
                <input
                  className={classes.control}
                  type='password'
                  name='new_password'
                  placeholder='Новый пароль'
                  value={values.new_password}
                  onChange={(event) => {
                    setFieldValue('new_password', event.target.value);
                    if (errors.new_password) setFieldTouched('new_password', false);
                  }}
                  onBlur={handleBlur}
                />
                {touched.new_password && errors.new_password ? (
                  <p className={`fig-field-error ${classes.error}`} role='alert'>
                    {errors.new_password}
                  </p>
                ) : null}
              </label>
              <label className={classes.cell}>
                <span className={classes.label}>Повторите новый пароль</span>
                <input
                  className={classes.control}
                  type='password'
                  name='repeat_new_password'
                  placeholder='Повторите новый пароль'
                  value={values.repeat_new_password}
                  onChange={(event) => {
                    setFieldValue('repeat_new_password', event.target.value);
                    if (errors.repeat_new_password) setFieldTouched('repeat_new_password', false);
                  }}
                  onBlur={handleBlur}
                />
                {touched.repeat_new_password && errors.repeat_new_password ? (
                  <p className={`fig-field-error ${classes.error}`} role='alert'>
                    {errors.repeat_new_password}
                  </p>
                ) : null}
              </label>
            </div>
            <PasswordRulesHint />
            <button
              className={`${classes.primary} ${classes.primaryFit}`}
              type='submit'
              disabled={changePasswordRequest.isPending || setPasswordRequest.isPending}
            >
              {needsSetPassword ? 'Задать пароль' : 'Изменить пароль'}
            </button>
          </section>
        )}
      </FormContainer>

      <PersonalDataChangeRequestSection />

      <section className={classes.card}>
        <h2 className={classes.cardTitle} data-icon="bell">Уведомления</h2>
        <p className={classes.body}>
          Разрешение на звук и всплывающие окна настраивается в браузере. Список событий - в
          разделе уведомлений.
        </p>
        <DesktopNotifySettings showTitle={false} />
        <TutorVkSettings />
        <Link to='/teacher/notifications' className={classes.secondary}>
          Открыть уведомления
        </Link>
      </section>

      <section className={classes.card}>
        <h2 className={classes.cardTitle} data-icon="user">Действия с аккаунтом</h2>
        <p className={classes.body}>Вы потеряете историю и контакты учеников.</p>
        <button type='button' className={`${classes.secondary} ${classes.secondaryFit}`} onClick={() => setModalOpen(true)}>
          Удалить
        </button>
        {modalOpen ? (
          <ConfirmModal
            onOk={() => {
              if (roleId) void removeRole.mutateAsync({ roleId });
            }}
            open={modalOpen}
            title='Удалить аккаунт?'
            text='Вы потеряете историю и контакты учеников. Действие нельзя отменить.'
            okLabel='Удалить'
            danger
            onClose={CloseModal}
          />
        ) : null}
      </section>
    </main>
  );
};
